import { useState, useRef, DragEvent, ChangeEvent } from 'react'
import { SUPPORTED_FILE_TYPES } from '../api/document-upload.api'

type FileDropzoneProps = {
  onFilesSelected: (files: File[]) => void
  disabled?: boolean
  isUploading?: boolean
}

export function FileDropzone({ onFilesSelected, disabled, isUploading }: FileDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const acceptedExtensions = SUPPORTED_FILE_TYPES.map(t => `.${t}`).join(',')

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragOver(true)
    }
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (disabled) return

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      onFilesSelected(files)
    }
  }

  function handleFileInput(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (files && files.length > 0) {
      onFilesSelected(Array.from(files))
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  function handleClick() {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
        transition-all duration-200 ease-in-out
        ${isDragOver
          ? 'border-theme-accent bg-primary-light'
          : 'border-default hover:border-theme-accent hover:bg-primary-light/50'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedExtensions}
        onChange={handleFileInput}
        disabled={disabled}
        className="hidden"
      />

      {isUploading ? (
        <div className="flex flex-col items-center">
          <div className="loading-spinner w-10 h-10 border-4 rounded-full mb-3" />
          <p className="text-sm text-theme-accent font-medium">Uploading documents...</p>
        </div>
      ) : (
        <>
          <div className={`mb-3 ${isDragOver ? 'text-theme-accent' : 'text-theme-muted'}`}>
            <svg
              className="mx-auto h-12 w-12"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <p className={`text-sm font-medium mb-1 ${isDragOver ? 'text-theme-accent' : 'text-theme-primary'}`}>
            {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
          </p>
          <p className="text-xs text-theme-muted mb-3">
            or click to browse
          </p>
          <p className="text-xs text-theme-muted">
            Supported: {SUPPORTED_FILE_TYPES.join(', ').toUpperCase()}
          </p>
        </>
      )}
    </div>
  )
}
